<script setup lang="ts">
import SpotifyArtistsSelect from '~/components/ui/SpotifyArtistsSelect.vue'
import VCard from '~/components/ui/VCard.vue'
import VText from '~/components/ui/VText.vue'

import '~/composables/useGetChildServerUpdates'

import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { areArraysEqual } from '~/utils/arrays'

import {
  providePatchBand,
  providePatchInfluencer,
} from '~/api-core/Account/AuthUser'

import type Spacing from '~/types/spacing'
import type SpotifyArtist from '~/types/spotifyArtist'
import type { UserKind } from '~/types/UserKind'

type Props = {
  userKind: UserKind
  userEntityId: number
  currentArtists?: SpotifyArtist[]
  padding?: Spacing
}

type Emits = {
  artists: [payload: SpotifyArtist[]]
}

const props = withDefaults(defineProps<Props>(), {
  padding: '10',
  currentArtists: () => [],
})
const emit = defineEmits<Emits>()

const { coreFetch } = useProvideCoreFetch()

const updatedArtists = ref<SpotifyArtist[]>([...props.currentArtists])

const maxArtists = computed<number>(() =>
  props.userKind === 'influencer' ? 20 : 5,
)

const shouldPatch = computed<boolean>(
  () =>
    !areArraysEqual(
      props.currentArtists.map((artist) => artist.id),
      updatedArtists.value.map((artist) => artist.id),
    ),
)

function handleInput(artists: SpotifyArtist[]) {
  updatedArtists.value = artists
  emit('artists', artists)
}

defineExpose({
  updatedArtists,
  ...useDefineChildUpdate(async function (): Promise<
    Partial<{ similar_artists: SpotifyArtist[] }>
  > {
    if (!shouldPatch.value) return {}

    const patchEntity =
      props.userKind === 'band'
        ? providePatchBand(coreFetch)
        : providePatchInfluencer(coreFetch)
    const payload = {
      similar_artists: updatedArtists.value,
    }

    await patchEntity(props.userEntityId, payload)

    return payload
  }),
})
</script>

<template>
  <VCard :padding="padding" class="sharedProfileEditSpotifyArtistsWrapper">
    <VText cfg="sans/24/bold" class="tw-mb-6 tw-block tw-text-black">{{
      $t(`${userKind}.profile.slug.edit.spotifyArtists00`)
    }}</VText>

    <SpotifyArtistsSelect
      id="editSpotifyArtists"
      data-test-id="spotifyArtistsSelect"
      :current-artists="currentArtists"
      :label="$t(`${userKind}.profile.slug.edit.spotifyArtists00`)"
      :err-msg="$t(`${userKind}.profile.slug.edit.spotifyArtists02`)"
      :max-artists="maxArtists"
      @update:model-value="handleInput"
    />
  </VCard>
</template>

<style lang="scss" scoped>
.expanded > .inputOuterWrap {
  max-width: unset;
}

.resultList {
  @apply tw-absolute tw-left-0 tw-z-10 tw-mb-12 tw-w-full tw-rounded tw-border tw-border-solid tw-border-gray-300 tw-bg-white;
  top: calc(100% + 8px);
}

.resultsWrap {
  @apply tw-overflow-y-auto tw-p-2;
  max-height: 280px;
  min-height: 58px;
}

.noResultsPlaceholder {
  @apply tw-flex tw-items-center tw-justify-center tw-text-gray-400;
  height: 54px;
}
</style>
